/**
 * Nested Profile View
 * JAISIU-878: UI components for rendering nested profile structures
 */

import { html, nothing, type TemplateResult } from "lit";
import type { ExecutionProfile } from "./types";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface NestedProfileDisplay {
  profileId: string;
  name: string;
  depth: number;
  isExpanded?: boolean;
  children: NestedProfileDisplay[];
  hasCircularRef?: boolean;
}

export interface ProfileTreeNode {
  profileId: string;
  name: string;
  depth: number;
  children: ProfileTreeNode[];
  isExpanded?: boolean;
  hasCircularRef?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Rendering Functions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Render a nested profile with indentation based on depth.
 *
 * @param profile - The profile to render
 * @param depth - Current nesting depth (for indentation)
 * @param isExpanded - Whether nested children are expanded
 * @param onToggle - Callback when expand/collapse is clicked
 * @returns Lit HTML template
 */
export function renderNestedProfile(
  profile: NestedProfileDisplay,
  depth: number = 0,
  isExpanded?: boolean,
  onToggle?: (profileId: string) => void,
  // JAISIU-types-sweep: the return type is annotated because the
  // function is directly recursive (`profile.children.map(child =>
  // renderNestedProfile(...))` in its own body). Without an
  // annotation TypeScript cannot infer it and reports TS7023 /
  // TS7024 (implicit `any`) under `strict`. `TemplateResult` is lit's
  // tagged-template result type — exactly what `html` returns.
): TemplateResult {
  const indent = depth * 16; // 16px per level
  const hasChildren = profile.children && profile.children.length > 0;

  return html`
    <div class="nested-profile" style="padding-left: ${indent}px;">
      <div class="nested-profile-row">
        ${
          hasChildren
            ? expandCollapseButton(
                profile.profileId,
                isExpanded ?? profile.isExpanded ?? false,
                onToggle,
              )
            : html`
                <span class="nested-profile-spacer"></span>
              `
        }

        <span class="nested-profile-icon">
          ${
            profile.hasCircularRef
              ? html`
                  <span class="nested-profile-icon--error" title="Circular reference">⟳</span>
                `
              : html`
                  <span class="nested-profile-icon--profile">⬡</span>
                `
          }
        </span>

        <span class="nested-profile-name ${profile.hasCircularRef ? "nested-profile-name--error" : ""}">
          ${profile.name}
        </span>

        <span class="nested-profile-id mono muted">
          ${profile.profileId}
        </span>

        ${depth > 0 ? html`<span class="nested-badge">depth ${depth}</span>` : nothing}
      </div>

      ${
        hasChildren && (isExpanded ?? profile.isExpanded)
          ? html`
            <div class="nested-profile-children">
              ${profile.children.map((child) =>
                renderNestedProfile(child, depth + 1, child.isExpanded, onToggle),
              )}
            </div>
          `
          : nothing
      }
    </div>
  `;
}

/**
 * Recursively render a tree of profiles.
 *
 * @param profiles - Array of profiles at the root level
 * @param rootId - Root profile ID to start from
 * @param expandedIds - Set of expanded profile IDs
 * @param onToggle - Callback for expand/collapse
 * @returns Lit HTML template
 */
export function renderNestedTree(
  profiles: NestedProfileDisplay[],
  rootId: string,
  expandedIds?: Set<string>,
  onToggle?: (profileId: string) => void,
): unknown {
  const rootProfile = profiles.find((p) => p.profileId === rootId);

  if (!rootProfile) {
    return html`<div class="nested-profile-empty">Profile not found: ${rootId}</div>`;
  }

  const isExpanded = expandedIds?.has(rootProfile.profileId) ?? rootProfile.isExpanded ?? false;

  return renderNestedProfile(rootProfile, 0, isExpanded, onToggle);
}

/**
 * Generate an expand/collapse button for nested profiles.
 *
 * @param profileId - The profile ID this button controls
 * @param isExpanded - Current expanded state
 * @param onToggle - Callback when button is clicked
 * @returns Lit HTML template for the button
 */
export function expandCollapseButton(
  profileId: string,
  isExpanded: boolean,
  onToggle?: (profileId: string) => void,
  // JAISIU-types-sweep: annotated for the same reason as
  // `renderNestedProfile` — it is reached from inside that recursive
  // function's body, so TypeScript needs a declared result type to
  // break the inference cycle (TS7024).
): TemplateResult {
  return html`
    <button
      class="nested-toggle ${isExpanded ? "nested-toggle--expanded" : "nested-toggle--collapsed"}"
      @click=${() => onToggle?.(profileId)}
      title="${isExpanded ? "Collapse" : "Expand"}"
      aria-expanded="${isExpanded}"
      data-profile-id="${profileId}"
    >
      ${isExpanded ? "▼" : "▶"}
    </button>
  `;
}

/**
 * Render a list of profiles with expand/collapse functionality.
 *
 * @param profiles - All available profiles
 * @param selectedId - Currently selected profile ID
 * @param expandedIds - Set of expanded profile IDs
 * @param onSelect - Callback when a profile is selected
 * @param onToggle - Callback when expand/collapse is clicked
 * @returns Lit HTML template
 */
export function renderProfileList(
  profiles: NestedProfileDisplay[],
  selectedId?: string,
  expandedIds?: Set<string>,
  onSelect?: (profileId: string) => void,
  onToggle?: (profileId: string) => void,
) {
  if (profiles.length === 0) {
    return html`
      <div class="nested-profile-list-empty">
        <div class="muted">No profiles available.</div>
      </div>
    `;
  }

  return html`
    <div class="nested-profile-list">
      <div class="nested-profile-list-header">
        <span>Profiles</span>
        <span class="muted">${profiles.length} total</span>
      </div>
      <div class="nested-profile-list-items">
        ${profiles.map((profile) => {
          const isExpanded = expandedIds?.has(profile.profileId) ?? profile.isExpanded ?? false;
          const isSelected = profile.profileId === selectedId;

          return html`
            <div
              class="nested-profile-list-item ${isSelected ? "nested-profile-list-item--selected" : ""}"
              @click=${() => onSelect?.(profile.profileId)}
            >
              ${renderNestedProfile(profile, 0, isExpanded, onToggle)}
            </div>
          `;
        })}
      </div>
    </div>
  `;
}

/**
 * Render validation status for nested profiles.
 *
 * @param cycles - Array of circular reference paths
 * @param maxDepth - Current maximum depth
 * @param maxAllowedDepth - Maximum allowed depth
 * @returns Lit HTML template
 */
export function renderNestingValidation(
  cycles: string[][],
  maxDepth: number,
  maxAllowedDepth: number,
) {
  const isValid = cycles.length === 0 && maxDepth <= maxAllowedDepth;

  return html`
    <div class="nested-validation ${isValid ? "nested-validation--valid" : "nested-validation--invalid"}">
      <div class="nested-validation-header">
        <span class="nested-validation-icon">${isValid ? "✓" : "✗"}</span>
        <span>${isValid ? "Valid" : "Issues Found"}</span>
      </div>

      <div class="nested-validation-details">
        ${
          cycles.length > 0
            ? html`
              <div class="nested-validation-issue">
                <span class="nested-badge nested-badge--error">
                  ${cycles.length} circular reference${cycles.length !== 1 ? "s" : ""}
                </span>
                <div class="nested-validation-cycles">
                  ${cycles.map(
                    (cycle) => html`
                      <div class="nested-cycle mono">
                        ${cycle.join(" → ")}
                      </div>
                    `,
                  )}
                </div>
              </div>
            `
            : nothing
        }

        ${
          maxDepth > maxAllowedDepth
            ? html`
              <div class="nested-validation-issue">
                <span class="nested-badge nested-badge--error">
                  Depth exceeded: ${maxDepth} > ${maxAllowedDepth}
                </span>
              </div>
            `
            : html`
              <div class="nested-validation-depth">
                <span class="nested-badge nested-badge--ok">
                  Depth: ${maxDepth}/${maxAllowedDepth}
                </span>
              </div>
            `
        }
      </div>
    </div>
  `;
}

/**
 * Render a simplified breadcrumb path for nested execution.
 *
 * @param path - Array of profile IDs from root to current
 * @param currentDepth - Current depth index
 * @param onNavigate - Callback when a breadcrumb is clicked
 * @returns Lit HTML template
 */
export function renderBreadcrumb(
  path: { profileId: string; name: string }[],
  currentDepth: number,
  onNavigate?: (profileId: string) => void,
) {
  return html`
    <div class="nested-breadcrumb">
      ${path.map((item, index) => {
        const isCurrent = index === currentDepth;
        const isPast = index < currentDepth;

        return html`
          ${
            index > 0
              ? html`
                  <span class="nested-breadcrumb-sep">›</span>
                `
              : nothing
          }
          <span
            class="nested-breadcrumb-item ${isCurrent ? "nested-breadcrumb-item--current" : ""} ${isPast ? "nested-breadcrumb-item--past" : ""}"
            @click=${() => !isCurrent && onNavigate?.(item.profileId)}
          >
            ${item.name}
          </span>
        `;
      })}
    </div>
  `;
}

/**
 * Render the expanded graph view for a profile.
 *
 * @param graph - The expanded execution graph
 * @param selectedNodeId - Currently selected node ID
 * @param onNodeSelect - Callback when a node is selected
 * @returns Lit HTML template
 */
export function renderExpandedGraph(
  graph: {
    id: string;
    name: string;
    nodes: Array<{
      id: string;
      name: string;
      role: string;
      type: string;
      metadata?: Record<string, unknown>;
    }>;
    edges: Array<{
      from: string;
      to: string;
      type: string;
    }>;
  },
  selectedNodeId?: string,
  onNodeSelect?: (nodeId: string) => void,
) {
  return html`
    <div class="nested-graph">
      <div class="nested-graph-header">
        <span>Expanded Graph</span>
        <span class="muted">${graph.nodes.length} nodes</span>
      </div>

      <div class="nested-graph-nodes">
        ${graph.nodes.map((node, index) => {
          const isSelected = node.id === selectedNodeId;
          const isWrapper = node.type === "nested-profile-wrapper";
          const nestedDepth = node.metadata?.nestedDepth as number | undefined;

          return html`
            <div class="nested-graph-node ${isSelected ? "nested-graph-node--selected" : ""} ${isWrapper ? "nested-graph-node--wrapper" : ""}">
              ${
                index < graph.nodes.length - 1
                  ? html`
                      <div class="nested-graph-connector">↓</div>
                    `
                  : nothing
              }

              <div
                class="nested-graph-node-content"
                @click=${() => onNodeSelect?.(node.id)}
              >
                <div class="nested-graph-node-header">
                  ${
                    isWrapper
                      ? html`
                          <span class="nested-graph-node-icon">⟳</span>
                        `
                      : html`
                          <span class="nested-graph-node-icon">⬡</span>
                        `
                  }
                  <span class="nested-graph-node-name">${node.name}</span>
                </div>
                <div class="nested-graph-node-meta mono muted">
                  ${node.role}
                  ${
                    nestedDepth !== undefined
                      ? html`<span class="nested-badge">depth ${nestedDepth}</span>`
                      : nothing
                  }
                </div>
                ${
                  node.metadata?.nestedProfileId
                    ? html`
                      <div class="nested-graph-node-nested mono muted">
                        → ${node.metadata.nestedProfileId}
                      </div>
                    `
                    : nothing
                }
              </div>
            </div>
          `;
        })}
      </div>
    </div>
  `;
}

// ─────────────────────────────────────────────────────────────────────────────
// Styles (for use in component CSS)
// ─────────────────────────────────────────────────────────────────────────────

export const NESTED_PROFILE_STYLES = `
  .nested-profile {
    padding: 4px 0;
  }

  .nested-profile-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 8px;
    border-radius: 4px;
    transition: background-color 0.15s ease;
  }

  .nested-profile-row:hover {
    background-color: var(--bg-hover, #f5f5f5);
  }

  .nested-profile-spacer {
    width: 20px;
    display: inline-block;
  }

  .nested-toggle {
    width: 20px;
    height: 20px;
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    font-size: 10px;
    color: var(--text-muted, #888);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 3px;
    transition: background-color 0.15s ease;
  }

  .nested-toggle:hover {
    background-color: var(--bg-hover, #f0f0f0);
  }

  .nested-profile-icon {
    font-size: 14px;
  }

  .nested-profile-icon--profile {
    color: var(--accent, #6366f1);
  }

  .nested-profile-icon--error {
    color: var(--error, #ef4444);
  }

  .nested-profile-name {
    font-weight: 500;
  }

  .nested-profile-name--error {
    color: var(--error, #ef4444);
    text-decoration: line-through;
  }

  .nested-profile-id {
    font-size: 11px;
    margin-left: auto;
  }

  .nested-badge {
    font-size: 10px;
    padding: 2px 6px;
    border-radius: 10px;
    background-color: var(--bg-muted, #e5e5e5);
    color: var(--text-muted, #888);
  }

  .nested-badge--error {
    background-color: var(--error-bg, #fee2e2);
    color: var(--error, #ef4444);
  }

  .nested-badge--ok {
    background-color: var(--success-bg, #dcfce7);
    color: var(--success, #22c55e);
  }

  .nested-validation {
    padding: 12px;
    border-radius: 6px;
    margin-top: 12px;
  }

  .nested-validation--valid {
    background-color: var(--success-bg, #dcfce7);
    border: 1px solid var(--success, #22c55e);
  }

  .nested-validation--invalid {
    background-color: var(--error-bg, #fee2e2);
    border: 1px solid var(--error, #ef4444);
  }

  .nested-validation-header {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    margin-bottom: 8px;
  }

  .nested-validation-icon {
    font-size: 16px;
  }

  .nested-cycle {
    font-size: 11px;
    padding: 4px 8px;
    background-color: rgba(0, 0, 0, 0.05);
    border-radius: 3px;
    margin-top: 4px;
  }
`;
